/**
 * Single-sourced install-snippet builders. The onboarding `InstallStep`
 * and the `Settings → Endpoint` rotate-key flow both render the embed
 * snippet keyed to a site's `site_key`; building it in one place keeps
 * the CDN URL, attribute name, and copy identical wherever the merchant
 * encounters it (a drifted snippet is the exact failure these surfaces
 * exist to catch).
 *
 * ADR-0043 §2.1 — the install snippet carries `data-proof-token`
 * alongside `data-site-key`. The embed RUNTIME does not read or
 * exfiltrate the proof token; the attribute exists only so origin's
 * `proof-check` server-fetch can find it in the merchant's served HTML
 * (the snippet-presence proof). The proof token is per-site, opaque,
 * 256 bits — public-by-design once installed.
 *
 * 2026-10-26 feedback — the canonical paste snippet is now a POST-LOAD
 * inline loader instead of a bare `<script async src="…cdn.sill.so…">`.
 * The old async tag delayed the merchant's `window.load` event until
 * `cdn.sill.so/embed.js` finished downloading; if the CDN is slow or
 * black-holed, every third-party script/analytics the merchant hangs on
 * `load` waited too. The founder HARD RULE is: Sill must NEVER slow a
 * merchant site, EVER — every failure must behave as if Sill weren't
 * installed. The inline loader:
 *   - keeps `data-site-key` / `data-proof-token` on the inline `<script>`
 *     so static-HTML scanners (install verification, readiness scanner,
 *     "installed" detection) still see them exactly as before;
 *   - creates the real `<script src="https://cdn.sill.so/embed.js">` on
 *     `window.load` — or on the next tick if the document is already
 *     `complete` (GTM / Tag Manager / any post-load injection path);
 *   - uses a page-global `window.__sill_ldr` guard so the Worker and
 *     WordPress-plugin copies share the same guard and never load the
 *     real script twice;
 *   - copies `data-site-key` / `data-proof-token` / `data-sill-consent`
 *     onto the dynamic `<script>` so the embed's `readSiteKey` /
 *     `withVisitorIds` paths work unchanged.
 *
 * The old async-src tag is still a valid install for sites with a
 * Content-Security-Policy that forbids inline scripts
 * ({@link buildClassicScriptSnippet}); install verification and the
 * scanner recognise both forms.
 */

/** The default script-tag embed — the snippet >95% of merchants paste.
 *  The inline loader never blocks `window.load`; see module docstring. */
// The copied code must never contain a literal end-of-body tag, even in
// a comment: Shopify inserts its app embeds before the first one it finds
// in theme.liquid, so a pasted comment swallowed a store's app scripts
// and left a stray "-->" on the page (netalinissim.com, 2026-10-29).
export function buildScriptSnippet(
  site_key: string,
  proof_token?: string,
): string {
  const proof_attr =
    typeof proof_token === 'string' && proof_token.length > 0
      ? ` data-proof-token="${proof_token}"`
      : '';
  return `<!-- Sill -->
<script data-site-key="${site_key}"${proof_attr}>
!function(){if(window.__sill_ldr)return;window.__sill_ldr=1;var c=document.currentScript;function g(){var s=document.createElement("script"),d=c&&c.dataset||{};s.async=1;s.src="https://cdn.sill.so/embed.js";if(d.siteKey)s.dataset.siteKey=d.siteKey;if(d.proofToken)s.dataset.proofToken=d.proofToken;if(d.sillConsent)s.dataset.sillConsent=d.sillConsent;(document.head||document.documentElement).appendChild(s)}document.readyState==="complete"?setTimeout(g,0):addEventListener("load",g,{once:true})}();
</script>`;
}

/**
 * Classic bare-src `<script async src="…cdn.sill.so…">` snippet — only
 * for merchants whose Content-Security-Policy forbids inline scripts
 * (no `'unsafe-inline'` on `script-src`). Blocks `window.load` on the
 * embed.js download; prefer {@link buildScriptSnippet}. Install
 * verification, the readiness scanner and the proof-check predicate
 * recognise both forms.
 */
export function buildClassicScriptSnippet(
  site_key: string,
  proof_token?: string,
): string {
  const proof_attr =
    typeof proof_token === 'string' && proof_token.length > 0
      ? `\n        data-proof-token="${proof_token}"`
      : '';
  return `<!-- Sill (strict-CSP version) -->
<script async src="https://cdn.sill.so/embed.js"
        data-site-key="${site_key}"${proof_attr}></script>`;
}

/**
 * ADR-0056 — hash-form embed snippet for Shopify manual install (and
 * any future post-OAuth manual-install surface). Resolves identically
 * at the edge to the raw-key form (ADR-0055 widened the resolver to
 * accept a bare 43-char `site_key_hash` directly as `?site=`). The
 * canonical post-OAuth manual artifact for Shopify — the raw
 * `site_key` is shown-once at creation per ADR-0010 §7.2 and is gone
 * from session post-onboarding; the hash is fetchable from the
 * tenant-scoped GET /v1/sites/:site_id read.
 *
 * `cdn_origin` is taken explicitly so unit tests can pin it; the
 * production caller passes `'https://cdn.sill.so'` (the same origin
 * the auto-installed ScriptTag points at).
 *
 * Parallel to {@link buildScriptSnippet}; the two builders are sibling
 * by design (ADR-0056 §4 — different snippet shape, different verify
 * primitive, different surrounding instructions). No conditional inside
 * either — the call site selects which builder to use.
 */
export function buildHashFormScriptSnippet(
  site_key_hash: string,
  cdn_origin: string,
): string {
  const u = new URL('/embed.js', cdn_origin);
  u.searchParams.set('site', site_key_hash);
  // Same post-load loader as buildScriptSnippet, so a theme.liquid paste
  // never holds up the store's `load` event either.
  return `<script>!function(){if(window.__sill_ldr)return;window.__sill_ldr=1;function g(){var s=document.createElement("script");s.async=1;s.src="${u.toString()}";(document.head||document.documentElement).appendChild(s)}document.readyState==="complete"?setTimeout(g,0):addEventListener("load",g,{once:true})}();</script>`;
}

/**
 * WordPress manual-paste fallback — the SAME embed script as every other
 * platform. The recommended WordPress path is the per-site-keyed Sill
 * plugin download (ADR-0188, served by the API's agent-ready endpoint);
 * this snippet is the alternative for merchants who prefer to paste the
 * tag themselves. The WordPress-specific part is WHERE to paste, rendered
 * as instruction copy by the panel; the snippet itself is byte-identical
 * to {@link buildScriptSnippet}.
 */
export function buildWordPressSnippet(
  site_key: string,
  proof_token?: string,
): string {
  return buildScriptSnippet(site_key, proof_token);
}

/** Identify-on-request, server-side ingest call. */
export function buildManualSnippet(site_key: string): string {
  return `# Identify-on-request, server-side
POST https://edge.sill.so/v1/discovery/ingest
Headers: x-sill-site-key: ${site_key}
Body:    { "url": "<current url>", "user_agent": "<UA>" }`;
}

/**
 * ADR-0097 Phase A — the site's signed agent-card URL + the STATIC
 * discovery pointer snippet. The embed injects this <link> automatically
 * at runtime; the static paste covers agents that fetch raw HTML without
 * executing JS (most crawler-style agents). Idempotent with the runtime
 * injection — the embed skips when a link[rel="agent-card"] exists.
 */
// A raw site key is `{region}_{43-char base64url}` — `us_`/`eu_` since the
// ADR-0187 residency change; the legacy `sk_` prefix and a bare 43-char
// hash still resolve. The optional 2-4 char lowercase region prefix
// future-proofs against new regions without another client change. (Was
// `/^(sk_)?[A-Za-z0-9_-]{43}$/` pre-residency, which rejected every
// newly-minted us_/eu_ key → dead card link + no-op download; 2026-07-28.)
export const AGENT_CARD_KEY_RE = /^([a-z]{2,4}_)?[A-Za-z0-9_-]{43}$/;

/** Whether a value is a usable agent-card key (a region-prefixed or bare
 *  43-char token). Callers gate the card download/preview on this so a
 *  present-but-malformed or missing key never yields an
 *  `{"error":"invalid_site_key"}` card (founder 2026-07-16: `has_key` only
 *  checked length>0, which was weaker). */
export function isValidAgentCardKey(key: string | undefined | null): boolean {
  return typeof key === 'string' && AGENT_CARD_KEY_RE.test(key);
}

export function buildAgentCardUrl(site_key: string): string {
  // Defense-in-depth (security review minor 3): the value comes from
  // Sill's own API today, but the builder is exported — pin the shape
  // so a future caller can't interpolate arbitrary text into a
  // clipboard-pasted snippet.
  if (!AGENT_CARD_KEY_RE.test(site_key)) return 'https://edge.sill.so/v1/agent-card/invalid.json';
  return `https://edge.sill.so/v1/agent-card/${site_key}.json`;
}

export function buildAgentCardSnippet(site_key: string): string {
  return `<!-- optional: paste in <head> so non-JS agents discover your agent card -->
<link rel="agent-card" type="application/json"
      href="${buildAgentCardUrl(site_key)}">`;
}

/**
 * ADR-0124 Story D1 — the site's signed ARD catalog URL. Same defense-
 * in-depth posture as {@link buildAgentCardUrl}: the value comes from
 * Sill's own API today, but the builder is exported — pin the shape so
 * a future caller can't interpolate arbitrary text into a clipboard-
 * pasted URL. Mirrors the agent-card key regex (raw `sk_<43>` or bare
 * 43-char hash — both resolve at the edge per ADR-0055).
 *
 * On a malformed `site_key` the builder returns the
 * `CATALOG_INVALID_SENTINEL_URL` value (NOT null — keeping the return
 * type `string` lets callers concatenate without nullish-handling).
 * Renderers MUST detect the sentinel via `isCatalogSentinelUrl` and
 * surface an unavailable / "Verify domain first" state instead of a
 * dead copyable link.
 */
export const CATALOG_INVALID_SENTINEL_URL =
  'https://edge.sill.so/v1/catalog/invalid.json';

export function buildCatalogUrl(site_key: string): string {
  if (!AGENT_CARD_KEY_RE.test(site_key)) return CATALOG_INVALID_SENTINEL_URL;
  return `https://edge.sill.so/v1/catalog/${site_key}.json`;
}

/**
 * Returns true iff `url` is the malformed-key sentinel returned by
 * {@link buildCatalogUrl}. The check is a literal string match (not a
 * suffix test) so an exotic `site_key` that ever decoded to a path
 * ending in `/invalid.json` could not collide.
 */
export function isCatalogSentinelUrl(url: string): boolean {
  return url === CATALOG_INVALID_SENTINEL_URL;
}

/**
 * The site's live MCP (Model Context Protocol) endpoint URL. Same
 * defense-in-depth posture as {@link buildAgentCardUrl} and
 * {@link buildCatalogUrl}: the value comes from Sill's own API today,
 * but the builder is exported — pin the shape so a future caller can't
 * interpolate arbitrary text into a clipboard-pasted URL. Mirrors the
 * agent-card key regex (raw `sk_<43>` or bare 43-char hash — both
 * resolve at the edge per ADR-0055).
 *
 * Unlike the agent-card and catalog URLs, the MCP endpoint has NO
 * `.json` suffix — it is a Streamable HTTP MCP server mounted at
 * `/v1/mcp/<site_key>`. It is also a POST endpoint, so callers
 * surfacing it in the UI MUST NOT render a browser "View" (GET) link:
 * a GET would 405. The renderer paints the copyable URL only with a
 * note that it goes into an MCP client config.
 *
 * On a malformed `site_key` the builder returns the
 * `MCP_INVALID_SENTINEL_URL` value (NOT null — keeping the return type
 * `string` lets callers concatenate without nullish-handling).
 * Renderers MUST detect the sentinel via {@link isMcpSentinelUrl} and
 * surface an unavailable / "Verify domain first" state instead of a
 * dead copyable link.
 */
export const MCP_INVALID_SENTINEL_URL = 'https://edge.sill.so/v1/mcp/invalid';

export function buildMcpUrl(site_key: string): string {
  if (!AGENT_CARD_KEY_RE.test(site_key)) return MCP_INVALID_SENTINEL_URL;
  return `https://edge.sill.so/v1/mcp/${site_key}`;
}

/**
 * Returns true iff `url` is the malformed-key sentinel returned by
 * {@link buildMcpUrl}. Literal string match — same posture as
 * {@link isCatalogSentinelUrl}.
 */
export function isMcpSentinelUrl(url: string): boolean {
  return url === MCP_INVALID_SENTINEL_URL;
}

/** Per-site signed-mandate endpoint at the edge (`POST /v1/m/:site_key/mandate`
 *  in `apps/edge/src/index.ts`). Same malformed-key sentinel posture as
 *  {@link buildMcpUrl}. */
export const MANDATE_INVALID_SENTINEL_URL = 'https://edge.sill.so/v1/m/invalid/mandate';

export function buildMandateUrl(site_key: string): string {
  if (!AGENT_CARD_KEY_RE.test(site_key)) return MANDATE_INVALID_SENTINEL_URL;
  return `https://edge.sill.so/v1/m/${site_key}/mandate`;
}

export function isMandateSentinelUrl(url: string): boolean {
  return url === MANDATE_INVALID_SENTINEL_URL;
}

/**
 * Per-site A2A messaging endpoint at the edge (`POST /v1/a2a/:site_key`).
 * Agents send a JSON-RPC `message/send` naming one of your skills, or
 * `tasks/get` / `tasks/cancel` to read or cancel a running task. Same
 * defense-in-depth sentinel posture as {@link buildMcpUrl}: the endpoint
 * is POST-only, so renderers paint the copyable URL without a "View" link.
 */
export const A2A_INVALID_SENTINEL_URL = 'https://edge.sill.so/v1/a2a/invalid';

export function buildA2aUrl(site_key: string): string {
  if (!AGENT_CARD_KEY_RE.test(site_key)) return A2A_INVALID_SENTINEL_URL;
  return `https://edge.sill.so/v1/a2a/${site_key}`;
}

export function isA2aSentinelUrl(url: string): boolean {
  return url === A2A_INVALID_SENTINEL_URL;
}

/**
 * Public PAP (Personal Agent Protocol) discovery file at the merchant's
 * own origin. The Worker/plugin install serves this; shoppers' personal
 * agents fetch it first to find Sill's issuer and token endpoints.
 * Returned as a plain string so this can also be rendered without a
 * site-key check (the file lives on the merchant's own domain).
 *
 * Alias kept so older imports keep compiling; new call sites should
 * use {@link buildPapOwnDomainUrl} for the own-domain path and
 * {@link buildPapSillUrl} for the authoritative edge URL.
 */
export function buildPapDiscoveryUrl(domain: string): string {
  return buildPapOwnDomainUrl(domain);
}

/* ------------------------------------------------------------------ */
/* Own-domain URL builders.                                            */
/*                                                                     */
/* These are the paths the Cloudflare Worker (ADR-0211) and the Sill   */
/* WordPress plugin serve on the merchant's registered domain. They    */
/* resolve to Sill content only when a server-side install is live;    */
/* callers that render these to merchants MUST gate on the install     */
/* coverage state (`InstallCoverage.server_side.state === 'live'`) and */
/* fall back to "not served yet" copy when the Worker/plugin is not    */
/* in place.                                                            */
/*                                                                     */
/* All builders accept a hostname, lowercase it, and reject malformed  */
/* values via the shared sentinel (same defense-in-depth posture as    */
/* the edge builders above).                                            */
/* ------------------------------------------------------------------ */

const OWN_DOMAIN_INVALID_SENTINEL = 'https://invalid.example';

function safeOwnDomain(domain: string): string | null {
  const d = typeof domain === 'string' ? domain.trim().toLowerCase() : '';
  if (!/^[a-z0-9][a-z0-9.-]{0,252}\.[a-z]{2,}$/.test(d)) return null;
  return d;
}

/** `https://{domain}/.well-known/agent-card.json` — the A2A agent card
 *  path the Worker/plugin proxies back to the signed edge card. */
export function buildAgentCardOwnDomainUrl(domain: string): string {
  const d = safeOwnDomain(domain);
  return d === null
    ? `${OWN_DOMAIN_INVALID_SENTINEL}/.well-known/agent-card.json`
    : `https://${d}/.well-known/agent-card.json`;
}

/** `https://{domain}/.well-known/ai-catalog.json` — the signed ARD
 *  catalog path the Worker/plugin proxies back to the edge catalog.
 *  A `/ai-catalog.json` alias also resolves to the same document. */
export function buildCatalogOwnDomainUrl(domain: string): string {
  const d = safeOwnDomain(domain);
  return d === null
    ? `${OWN_DOMAIN_INVALID_SENTINEL}/.well-known/ai-catalog.json`
    : `https://${d}/.well-known/ai-catalog.json`;
}

/** `https://{domain}/.well-known/mcp/server-card.json` — RFC-6415-style
 *  discovery pointer the Worker builds inline. The MCP endpoint itself
 *  has NO own-domain equivalent (it is a Streamable HTTP server at
 *  `edge.sill.so/v1/mcp/{site_key}`); this file points at it. */
export function buildMcpServerCardOwnDomainUrl(domain: string): string {
  const d = safeOwnDomain(domain);
  return d === null
    ? `${OWN_DOMAIN_INVALID_SENTINEL}/.well-known/mcp/server-card.json`
    : `https://${d}/.well-known/mcp/server-card.json`;
}

/** `https://{domain}/.well-known/poppy.json` — the PAP discovery file
 *  the Worker/plugin forwards to the edge PAP issuer. */
export function buildPapOwnDomainUrl(domain: string): string {
  const d = safeOwnDomain(domain);
  return d === null
    ? `${OWN_DOMAIN_INVALID_SENTINEL}/.well-known/poppy.json`
    : `https://${d}/.well-known/poppy.json`;
}

/**
 * The authoritative Sill PAP URL. Served regardless of install state;
 * agents can fetch it directly, or follow the own-domain pointer
 * served by the Worker/plugin. Mirrors the `buildAgentCardUrl` /
 * `buildCatalogUrl` / `buildMcpUrl` shape so the Endpoints panel can
 * render one Sill URL per protocol row.
 */
export const PAP_INVALID_SENTINEL_URL =
  'https://edge.sill.so/v1/poppy/invalid/poppy.json';

export function buildPapSillUrl(site_key: string): string {
  if (!AGENT_CARD_KEY_RE.test(site_key)) return PAP_INVALID_SENTINEL_URL;
  return `https://edge.sill.so/v1/poppy/${site_key}/poppy.json`;
}

export function isPapSentinelUrl(url: string): boolean {
  return url === PAP_INVALID_SENTINEL_URL;
}

/** 2026-10-27 — Sill-managed llms.txt and HTML agent page at the edge,
 *  plus the public JWKS. `null` for a key that fails the shape check. */
export function buildLlmsTxtUrl(site_key: string): string | null {
  return AGENT_CARD_KEY_RE.test(site_key) ? `https://edge.sill.so/v1/llms/${site_key}.txt` : null;
}

export function buildAgentPageUrl(site_key: string): string | null {
  return AGENT_CARD_KEY_RE.test(site_key) ? `https://edge.sill.so/v1/agent/${site_key}` : null;
}

export const SILL_JWKS_URL = 'https://edge.sill.so/.well-known/jwks.json';

/**
 * ADR-0181 — permanent discovery document for `/.well-known/agent-card.json`.
 * A POINTER, not a snapshot: it carries identity + the `endpoints.agentCard`
 * URL of the merchant's LIVE signed card, which is the single source of
 * truth for capabilities and auto-updates when the merchant changes skills.
 * There is deliberately NO static `capabilities` array here — embedding one
 * would go stale the moment the merchant edited skills, and an agent that
 * read it would act on outdated data (founder 2026-07-29). Agents read this
 * file, follow `agentCard`, and get the current skill set. Upload once,
 * never re-upload.
 */
export function buildDiscoveryJson(params: {
  readonly name: string;
  readonly url: string;
  readonly site_key: string;
}): string {
  if (!AGENT_CARD_KEY_RE.test(params.site_key)) {
    return JSON.stringify({ error: 'invalid_site_key' }, null, 2);
  }
  const doc = {
    schemaVersion: 'v1',
    name: params.name.length > 0 ? params.name : params.url,
    url: params.url,
    provider: { name: 'Sill', url: 'https://sill.so' },
    // The live signed card is authoritative for capabilities + skills and
    // updates automatically — always fetch it fresh; do not cache this doc's
    // shape as the skill set.
    endpoints: {
      agentCard: buildAgentCardUrl(params.site_key),
      mcp: buildMcpUrl(params.site_key),
      mandate: `https://edge.sill.so/v1/m/${params.site_key}/mandate`,
    },
  };
  return JSON.stringify(doc, null, 2);
}

/** Triggers a browser download of agent-card.json. Browser context only. */
export function downloadDiscoveryJson(params: {
  readonly name: string;
  readonly url: string;
  readonly site_key: string;
}): void {
  // Safety net: never download an `{"error":"invalid_site_key"}` file if the
  // key isn't loaded/valid (the key is shown once at creation). Callers also
  // gate their buttons on `isValidAgentCardKey`; this is the last line.
  if (!isValidAgentCardKey(params.site_key)) return;
  const json = buildDiscoveryJson(params);
  const blob = new Blob([json], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = 'agent-card.json';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}
